import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { validateLogin } from "../../services/validate-login";
import { getSelect2ResponseService, select3Service } from "../../services/ondcService";
import { Info } from "lucide-react";
import Loader from "../../components/common/Loader";
import { v4 as uuidv4 } from "uuid";

// LoanCard Component
function LoanCard({ logo, company, maxAmount, roi, tenure, event, message, selected, onSelect }: any) {
  const [showTooltip, setShowTooltip] = useState(false);

  const hasError = event && message; // true if error exists

  return (
    <div
      onClick={!hasError ? onSelect : undefined}
      className={`rounded-2xl shadow-md p-4 flex flex-col gap-4 transition relative 
        ${hasError 
          ? "bg-white border border-gray-200 cursor-default" 
          : selected 
            ? "bg-blue-100 border-2 border-blue-500 cursor-pointer" 
            : "bg-white border border-gray-200 cursor-pointer"}
      `}
    >
      {/* Row 1 - Logo + Company */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logo} alt={company} className="h-10 w-10 rounded-md" />
          <span className="font-semibold text-gray-800">{company}</span>
        </div>

        {/* Error Info Icon */}
        {hasError && (
          <div
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <Info className="w-5 h-5 text-blue-500 cursor-pointer" />
            {showTooltip && (
              <div className="absolute right-0 mt-2 w-48 p-2 text-sm bg-black text-white rounded-md shadow-lg z-10">
                {message}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Row 2 - Loan Details */}
      {!hasError ? (
        <div className="flex items-center justify-between text-sm text-gray-700">
          <div className="flex-1 text-center">
            <p className="text-gray-600">Max. Amount</p>
            <p className="font-bold">{maxAmount}</p>
          </div>

          <div className="border-l border-gray-400 h-10"></div>

          <div className="flex-1 text-center">
            <p className="text-gray-600">RoI(%)</p>
            <p className="font-bold">{roi}</p>
          </div>

          <div className="border-l border-gray-400 h-10"></div>

          <div className="flex-1 text-center">
            <p className="text-gray-600">Tenure</p>
            <p className="font-bold">{tenure}</p>
          </div>
        </div>
      ) : (
        <div className="text-sm text-red-500">Error</div>
      )}
    </div>
  );
}

function Form() {
  const [offers, setOffers] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [loading, setLoading] = useState(true); 
  const navigate = useNavigate();

  useEffect(() => {
    async function getStep() {
      if(Number(localStorage.getItem("step")) >= 5){} else{ navigate("/personal-loan") }
    }
    getStep();
  }, []);

  // Fetch offers from REST API
  useEffect(() => {
    async function fetchOffers() {
      try {
        const txnId = {
          transaction_id: localStorage.getItem("transaction_id"),
        };
        console.log("Get Offers");
        const response = await getSelect2ResponseService(txnId);
        if (response.success) {
          setOffers(response.message.response);
          console.log(offers)
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
      } finally {
        setLoading(false); // hide loader
      }
    }
    fetchOffers();
  }, []);

  const onClickNext = async () => {
    if (selectedCompany) {
      // console.log("Selected Company:", selectedCompany);
      localStorage.setItem("message_id", uuidv4());

      try {
        setLoading(true); // show loader
        var data = {
            "transaction_id": localStorage.getItem("transaction_id"),
            "message_id": localStorage.getItem("message_id"),
            "action": "select3",
            "bpp_id": selectedCompany.bpp_id,
            "bpp_uri": selectedCompany.bpp_uri,
            "provider_id": selectedCompany.provider_id,
            "item_id": selectedCompany.item_id,
            "form_id": selectedCompany.form_id,
            "form_url": selectedCompany.form_url,
            "prev_msg_id": selectedCompany.prev_msg_id,
            "version": selectedCompany.version,
            "request_amount": selectedCompany.max_amount,
            "request_term": selectedCompany.max_tenure
        }
        console.log(data)
        const response = await select3Service(data);
        if (response.success) {
          localStorage.setItem("step", "6")
          // wait for 5 seconds before navigating
          await new Promise((resolve) => setTimeout(resolve, 5000));
          navigate("/offer-details");
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
      } finally {
        setLoading(false); // hide loader
      }
    } else {
      alert("Please select a lender before proceeding.");
    }
  };

  // 👉 Show loader full screen while fetching
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f9ff]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f9ff] px-4">
      {/* Hero Section */}
      <div className="flex flex-col md:flex-row max-w-5xl w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Left side Image (fixed) */}
        <div className="basis-full md:basis-2/5 flex items-center justify-center p-6 bg-gray-50 md:h-[calc(100vh-100px)] md:sticky md:top-0">
          <img
            src="svg/img4.svg"
            alt="Working professional"
            className="w-full max-w-xs md:max-w-none object-contain"
          />
        </div>

        {/* Right side Form (scrollable) */}
        <div className="basis-full md:basis-3/5 flex flex-col p-8 bg-white relative md:h-[calc(100vh-100px)] md:overflow-y-auto">
          {/* Step Number */}
          <div className="absolute top-4 right-4 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
            Step 6
          </div>
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <h2 className="text-xl font-semibold mb-6">Offers by lenders</h2>
            <p className="text-sm text-gray-600 mb-4">
              {/* Congratulations!!! */}
              {/* <br /> */}
              Based on your eligibility, you may choose the best offers by the lenders
            </p>

            {/* Loan Offers */}
            <div className="space-y-4">
              {offers.map((offer, idx) => (
                <LoanCard
                  key={idx}
                  logo={offer.logo}
                  company={offer.name}
                  maxAmount={offer.max_amount}
                  roi={offer.interest_rate}
                  tenure={offer.max_tenure}
                  event={offer.event}
                  message={offer.message}
                  selected={selectedCompany === offer}
                  onSelect={() => setSelectedCompany(offer)}
                />
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-6 flex gap-4">
            <button
              className="flex-1 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
              onClick={() => navigate("/account-aggregator")}
            >
              <ArrowLeft size={18} /> Back
            </button>

            <button
              className={`flex-1 px-8 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2
                ${selectedCompany ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-300 text-gray-600 cursor-not-allowed"}
              `}
              onClick={onClickNext}
              disabled={!selectedCompany}
            >
              Next <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}


export default function Step5ChooseOfferbyLenders() {
  const navigate = useNavigate();

  useEffect(() => {
    validateLogin(navigate);
  }, [navigate]);

  return (
    <div className="flex flex-col h-screen overflow-y-auto">
      <div className="flex-shrink-0">
        <Header title="" />
      </div>

      {/* Main Content scrollable */}
      <main className="flex-1 overflow-y-auto">
          <Form />
      </main>
    </div>
  );
}
